/**
 * Value formatters.
 *
 * The single place these conversions happen. A `toFixed(2)` or a
 * `toLocaleDateString` inline in a component is a review failure.
 */
import type { Locale } from "@/lib/i18n";

/**
 * Joins the parts of a date. "7 Sept 2026" is one value: in a narrow table
 * column an ordinary space let it break into three lines, one word each.
 */
const NBSP = "\u00a0";

/**
 * Money is integer paise on the wire; rupees only exist at the display edge.
 *
 * Always formatted with en-IN grouping (₹2,50,000.00) regardless of UI
 * language -- that is how Indian staff read amounts.
 */
export function formatMoney(paise: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(paise / 100);
}

/** Rupee input -> integer paise, without ever holding a float. */
export function rupeesToPaise(input: string): number | null {
  const cleaned = input.replace(/[₹,\s]/g, "").trim();
  if (!/^\d*\.?\d{0,2}$/.test(cleaned) || cleaned === "" || cleaned === ".")
    return null;
  const [whole, fraction = ""] = cleaned.split(".");
  // Parsed as digits rather than parseFloat(x) * 100, which rounds badly.
  return (
    Number(whole || "0") * 100 + Number(fraction.padEnd(2, "0").slice(0, 2))
  );
}

/**
 * Paise back to the plain rupee string a form input holds.
 *
 * The inverse of `rupeesToPaise`, for prefilling an edit form. Integer
 * arithmetic rather than `paise / 100`, which produces 1234.5600000000001 for
 * values a treasurer then has to correct by hand.
 */
export function paiseToRupees(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return "";
  const sign = paise < 0 ? "-" : "";
  const abs = Math.abs(paise);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

const DATE_FORMATS: Record<Locale, Intl.DateTimeFormatOptions> = {
  en: { day: "numeric", month: "short", year: "numeric" },
  ml: { day: "numeric", month: "short", year: "numeric" },
};

/**
 * An ISO calendar date (`2026-03-11`) as staff read it.
 *
 * A calendar date is not an instant, and must not be run through one. Parsing
 * `2026-03-11` as local midnight and then rendering it in `Asia/Kolkata` moves
 * the day backwards for any viewer east of IST -- a date of birth printed one
 * day early on a certificate. So the components are pinned to UTC and rendered
 * in UTC: whatever day the backend stored is the day that prints, everywhere.
 *
 * `formatDateTime` below is the opposite case: a real instant, which genuinely
 * does belong in the reader's timezone.
 */
export function formatDate(
  iso: string | null | undefined,
  locale: Locale = "en",
): string {
  if (!iso) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return "—";
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale === "ml" ? "ml-IN" : "en-IN", {
    ...DATE_FORMATS[locale],
    timeZone: "UTC",
  })
    .format(date)
    .replace(/ /g, NBSP);
}

/**
 * A month, as "Sep 2026".
 *
 * Maasavari periods are months, so printing the first of the month as a
 * full date invites reading "1 Sep" as the day something was paid.
 *
 * Pinned to UTC for the same reason `formatDate` is: the backend stores a
 * plain date, and a reader east of UTC must not see the month before.
 */
export function formatMonth(
  iso: string | null | undefined,
  locale: Locale = "en",
): string {
  if (!iso) return "—";
  const match = /^(\d{4})-(\d{2})/.exec(iso);
  if (!match) return "—";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale === "ml" ? "ml-IN" : "en-IN", {
    year: "numeric",
    month: "short",
    timeZone: "UTC",
  })
    .format(date)
    .replace(/ /g, NBSP);
}

export function formatDateTime(
  iso: string | null | undefined,
  locale: Locale = "en",
): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale === "ml" ? "ml-IN" : "en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  }).format(date);
}

/** Counts use the active locale's numerals; money never does. */
export function formatCount(value: number, locale: Locale = "en"): string {
  return new Intl.NumberFormat(locale === "ml" ? "ml-IN" : "en-IN").format(
    value,
  );
}

/**
 * The twelve month names, in the reader's language.
 *
 * Three period pickers each built this list with the locale hard-coded to
 * English, so a Malayalam user choosing which month to bill, to pay a salary
 * for, or to raise a fee against read "January" in an otherwise Malayalam
 * form. Indexed 1-12, as the API numbers months.
 */
export function monthNames(locale: Locale = "en"): string[] {
  const format = new Intl.DateTimeFormat(locale === "ml" ? "ml-IN" : "en-IN", {
    month: "long",
    timeZone: "UTC",
  });
  return Array.from({ length: 12 }, (_, i) =>
    format.format(new Date(Date.UTC(2000, i, 1))),
  );
}

/**
 * Show the name in the reader's script when it exists, falling back to the
 * other. Never concatenates the two.
 */
export function personName(
  person: {
    name_en?: string | null | undefined;
    name_ml?: string | null | undefined;
  },
  locale: Locale = "en",
): string {
  const preferred = locale === "ml" ? person.name_ml : person.name_en;
  return preferred || person.name_ml || person.name_en || "—";
}

export function alternateName(
  person: {
    name_en?: string | null | undefined;
    name_ml?: string | null | undefined;
  },
  locale: Locale = "en",
): string | null {
  const primary = locale === "ml" ? person.name_ml : person.name_en;
  const other = locale === "ml" ? person.name_en : person.name_ml;
  return primary && other ? other : null;
}

/**
 * A Date as the local calendar date, YYYY-MM-DD.
 *
 * NOT `toISOString().slice(0, 10)`. That converts to UTC first, so local
 * midnight on 1 September in IST (UTC+5:30) serialises as 31 August. Every
 * date this product handles -- a payment date, a Friday collection, the start
 * of a reporting window -- is a local calendar date, never an instant.
 */
export function toLocalISODate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Today, as the local calendar date. */
export function todayISO(): string {
  return toLocalISODate(new Date());
}
